/**
 * Basari Mega Menu Engine
 * Purpose: Desktop hover-intent + keyboard controller for mega menu panels.
 * Sprint: 2
 * Future: Additional panel types stay data-attribute driven; extend selectors, not listeners.
 * Dependencies: [data-basari-mm-root], [data-basari-mm-trigger], [data-basari-mm-panel]
 */
(function () {
  'use strict';

  var DESKTOP_MQ = '(min-width: 990px)';
  var OPEN_DELAY = 80;
  var CLOSE_DELAY = 180;

  function BasariMegaMenu(root) {
    this.root = root;
    this.nav = document.querySelector('[data-basari-mm-nav]');
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-basari-mm-panel]'));
    this.activePanel = null;
    this.activeTrigger = null;
    this.openTimer = null;
    this.closeTimer = null;
    this.desktopMq = window.matchMedia(DESKTOP_MQ);

    this.onPointerOver = this.handlePointerOver.bind(this);
    this.onPointerOut = this.handlePointerOut.bind(this);
    this.onFocusIn = this.handleFocusIn.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onKeyDown = this.handleKeyDown.bind(this);
    this.onMqChange = this.handleMqChange.bind(this);

    this.bind();
    this.handleMqChange();
  }

  BasariMegaMenu.prototype.bind = function () {
    if (!this.nav) return;

    document.addEventListener('pointerover', this.onPointerOver);
    document.addEventListener('pointerout', this.onPointerOut);
    document.addEventListener('focusin', this.onFocusIn);
    document.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('keydown', this.onKeyDown);

    if (this.desktopMq.addEventListener) {
      this.desktopMq.addEventListener('change', this.onMqChange);
    } else if (this.desktopMq.addListener) {
      this.desktopMq.addListener(this.onMqChange);
    }
  };

  BasariMegaMenu.prototype.isDesktop = function () {
    return this.desktopMq.matches;
  };

  BasariMegaMenu.prototype.handleMqChange = function () {
    if (!this.isDesktop()) {
      this.close(true);
    }
  };

  BasariMegaMenu.prototype.clearTimers = function () {
    if (this.openTimer) {
      window.clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  };

  BasariMegaMenu.prototype.panelForTrigger = function (trigger) {
    if (!trigger) return null;
    var id = trigger.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  };

  BasariMegaMenu.prototype.closestTrigger = function (node) {
    return node && node.closest ? node.closest('[data-basari-mm-trigger]') : null;
  };

  BasariMegaMenu.prototype.closestPanel = function (node) {
    return node && node.closest ? node.closest('[data-basari-mm-panel]') : null;
  };

  BasariMegaMenu.prototype.isEngineNode = function (node) {
    if (!node || !node.closest) return false;
    return !!(
      node.closest('[data-basari-mm-trigger]') ||
      node.closest('[data-basari-mm-panel]') ||
      node.closest('[data-basari-mm-root]')
    );
  };

  BasariMegaMenu.prototype.handlePointerOver = function (event) {
    if (!this.isDesktop() || event.pointerType === 'touch') return;

    var trigger = this.closestTrigger(event.target);
    if (trigger && this.nav.contains(trigger)) {
      var panel = this.panelForTrigger(trigger);
      if (panel && this.root.contains(panel)) {
        this.scheduleOpen(trigger, panel);
      }
      return;
    }

    if (this.closestPanel(event.target) && this.root.contains(event.target)) {
      this.clearTimers();
    }
  };

  BasariMegaMenu.prototype.handlePointerOut = function (event) {
    if (!this.isDesktop() || event.pointerType === 'touch') return;

    var fromEngine = this.isEngineNode(event.target);
    if (!fromEngine) return;

    var toEngine = this.isEngineNode(event.relatedTarget);
    if (toEngine) return;

    this.scheduleClose();
  };

  BasariMegaMenu.prototype.scheduleOpen = function (trigger, panel) {
    var self = this;
    this.clearTimers();
    this.openTimer = window.setTimeout(function () {
      self.open(trigger, panel);
    }, OPEN_DELAY);
  };

  BasariMegaMenu.prototype.scheduleClose = function () {
    var self = this;
    this.clearTimers();
    this.closeTimer = window.setTimeout(function () {
      self.close(false);
    }, CLOSE_DELAY);
  };

  BasariMegaMenu.prototype.open = function (trigger, panel) {
    if (!this.isDesktop() || !trigger || !panel) return;

    if (this.activePanel && this.activePanel !== panel) {
      this.setPanelState(this.activePanel, this.activeTrigger, false);
    }

    this.root.hidden = false;
    this.root.setAttribute('aria-hidden', 'false');
    this.setPanelState(panel, trigger, true);
    this.activePanel = panel;
    this.activeTrigger = trigger;
  };

  BasariMegaMenu.prototype.close = function (immediate) {
    this.clearTimers();

    if (this.activePanel) {
      var trigger = this.activeTrigger;
      var focusTrigger =
        !immediate &&
        trigger &&
        document.activeElement &&
        this.root.contains(document.activeElement);

      this.setPanelState(this.activePanel, trigger, false);
      this.activePanel = null;
      this.activeTrigger = null;

      if (focusTrigger) {
        trigger.focus();
      }
    }

    this.root.hidden = true;
    this.root.setAttribute('aria-hidden', 'true');
  };

  BasariMegaMenu.prototype.setPanelState = function (panel, trigger, isOpen) {
    if (!panel) return;

    panel.classList.toggle('is-open', isOpen);
    panel.hidden = !isOpen;
    panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

    if (trigger) {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      var item = trigger.closest('.basari-header__nav-item');
      if (item) {
        item.classList.toggle('basari-header__nav-item--mega-open', isOpen);
      }
    }
  };

  BasariMegaMenu.prototype.handleFocusIn = function (event) {
    if (!this.isDesktop()) return;

    var trigger = this.closestTrigger(event.target);
    if (trigger && this.nav.contains(trigger)) {
      var panel = this.panelForTrigger(trigger);
      if (panel) this.open(trigger, panel);
      return;
    }

    if (this.closestPanel(event.target) && this.root.contains(event.target)) {
      this.clearTimers();
    }
  };

  BasariMegaMenu.prototype.handleFocusOut = function () {
    if (!this.isDesktop()) return;
    var self = this;
    window.requestAnimationFrame(function () {
      var active = document.activeElement;
      if (!active || !self.isEngineNode(active)) {
        self.scheduleClose();
      }
    });
  };

  BasariMegaMenu.prototype.handleKeyDown = function (event) {
    if (!this.isDesktop()) return;

    if (event.key === 'Escape' && this.activePanel) {
      event.preventDefault();
      var trigger = this.activeTrigger;
      this.close(true);
      if (trigger) trigger.focus();
      return;
    }

    var trigger = this.closestTrigger(event.target);
    if (!trigger || !this.nav.contains(trigger)) return;

    var panel = this.panelForTrigger(trigger);
    if (!panel) return;

    if (event.key === ' ') {
      event.preventDefault();
      if (this.activePanel === panel) {
        this.close(true);
        trigger.focus();
      } else {
        this.open(trigger, panel);
        this.focusFirstInPanel(panel);
      }
      return;
    }

    if (event.key === 'Enter' && this.activePanel !== panel) {
      event.preventDefault();
      this.open(trigger, panel);
      this.focusFirstInPanel(panel);
    }
  };

  BasariMegaMenu.prototype.focusFirstInPanel = function (panel) {
    if (!panel) return;
    var focusable = panel.querySelector(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable) focusable.focus();
  };

  function init() {
    var roots = document.querySelectorAll('[data-basari-mm-root]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.basariMmBound === 'true') return;
      root.dataset.basariMmBound = 'true';
      new BasariMegaMenu(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
